/**
 * 在线目录 / 隐身 / 中继目录客户端（ARCH §3.6）。
 *
 * 只是 `Libp2pBrowserPeer` 的薄封装：把「目录」这组语义从传输细节里剥离出来，
 * 便于 `RelayService` 直接消费，也便于单测注入假 peer。
 */

import type { OnlinePeer, RelayEntry } from '@shared/types';
import type { Libp2pBrowserPeer } from './peer';
import { createLogger } from '@/lib/logger';

const logger = createLogger('net:directory');

/** 目录客户端。 */
export class DirectoryClient {
  /** @param getPeer 取当前 peer（未连接时返回 null） */
  constructor(private readonly getPeer: () => Libp2pBrowserPeer | null) {}

  get available(): boolean {
    return this.getPeer() !== null;
  }

  /**
   * 上报可见性。
   * @param visible false = 隐身模式（**不出现在他人在线目录**）
   */
  async setVisible(visible: boolean, name: string): Promise<void> {
    const peer = this.getPeer();
    if (!peer) {
      logger.warn('未连接中继，跳过可见性上报');
      return;
    }
    try {
      await peer.setVisible(visible, name);
    } catch (err) {
      logger.warn('可见性上报失败', { err });
    }
  }

  /** 拉取在线用户（服务端已过滤隐身者与自身）。 */
  async onlinePeers(): Promise<OnlinePeer[]> {
    const peer = this.getPeer();
    if (!peer) return [];
    try {
      return (await peer.getOnlinePeers()).peers;
    } catch (err) {
      logger.warn('在线目录拉取失败', { err });
      return [];
    }
  }

  /** 拉取可用中继列表（多中继切换用）。 */
  async relays(): Promise<RelayEntry[]> {
    const peer = this.getPeer();
    if (!peer) return [];
    try {
      return await peer.getRelayDirectory();
    } catch (err) {
      logger.warn('中继目录拉取失败', { err });
      return [];
    }
  }
}

export default DirectoryClient;
